/**
 * WHAT IS INSIDE A CARD, DERIVED FROM WHAT THE PERSON MAY DO IN IT.
 *
 * Canon 1.3: "the card is a door, the permission is what determines what's
 * actually behind it once opened."
 *
 * Read literally, that sentence is a build instruction and not just a security
 * model. A card's sections ARE its permissions — so this file does not hold a
 * list of sections per card, invents no headings the database cannot justify,
 * and cannot drift from admin_card_permissions, because it never sees a card
 * slug at all. It is handed the permissions a person holds and turns them into
 * the sections that person should see.
 *
 * WHAT THIS REPLACES
 *
 * A flat list under the heading "What your account may do here" — accurate, and
 * a settings page. Twelve of the fourteen cards opened to nothing else, so a
 * finance officer opening Finance was told what they were permitted to do and
 * given no way to do any of it.
 *
 * THREE STATES, AND THE THIRD IS THE HONEST ONE
 *
 *   BUILT      the section renders its real screen.
 *   SHARED     several permissions open the same screen — a directory is one
 *              screen whether you may read it, edit it, or both — so it is
 *              rendered once, under the permission that owns it, and the others
 *              say which section they act inside.
 *   NOT BUILT  named, described in the permission's own words, and said plainly
 *              to be unbuilt. Not hidden: a card that silently omits what it
 *              cannot yet do makes the gap invisible to whoever has to close
 *              it, and makes the portal look smaller than its plan.
 *
 * NOTHING HERE INVENTS A CAPABILITY. If a section is not built, this file says
 * so rather than rendering an empty table, which reads as a working module with
 * no work in it — a different and much worse lie.
 */

/**
 * The surface each permission opens, where one exists.
 *
 * Keyed by permission rather than by card, because that is the level canon
 * puts the meaning at. `owner` is the permission that renders the screen when
 * several open the same one; the rest carry `sharedWith` and render a line
 * saying where their capability lives.
 */
export const SURFACES = {
  // Compliance. Two queues, in the order the work actually happens: an
  // application is somebody asking to exist on Orchid at all; verification is
  // what a business that already exists clears before a client is told it is
  // verified.
  view_kyc_queue:       { surface: 'provider_applications', owner: true },
  approve_verification: { surface: 'provider_verification', owner: true },
  flag_document_issue:  { surface: 'provider_verification' },

  // Compliance's third section, and the one that is not a queue. The two above
  // answer "what is waiting for me"; this answers "show me the documents of the
  // person on the phone", which is a different errand and was previously
  // unreachable -- a compliance officer could open a document only if the
  // subject happened to be sitting in the verification queue that day.
  view_kyc_document:    { surface: 'subject_documents', owner: true },

  // Compliance's fourth section, and the one that is a legal obligation rather
  // than a workflow. FIC Act s26A. Every new profile has been screened against
  // the sanctions list since script 29 and the matches were readable by nobody:
  // the four tables gate on manage_sanctions_list, which was named by four RLS
  // policies and present in no row of the permissions catalogue, so it could
  // not be attached to a role or to this card. Script 205 registers it.
  manage_sanctions_list: { surface: 'sanctions_review', owner: true },

  // Compliance's fifth. The founder found his own ID attached to a Callout
  // technician test account rather than to the provider profile his email
  // signs into, and nothing in the platform could move it -- the one function
  // that looked like it could, anonymize_profile, has never worked: it sets
  // profiles.deleted_at, a column that does not exist, and raises 42703 on
  // every call. Script 268 gives it a real writer, and widens the footprint so
  // a desk can SEE the second profile at all.
  reattach_an_identity:  { surface: 'identity_reattach', owner: true },

  // Compliance's sixth, and the one the landlord cohort waits on. Script 245
  // registered record_ownership_proof and built nothing behind it -- "after
  // this script the seat EXISTS and is empty". Until script 276 a landlord
  // could submit a title deed and no screen anywhere listed it, no code path
  // could refuse it, and the only route past it was the 'all' holder calling
  // an RPC by hand with a property UUID.
  record_ownership_proof: { surface: 'ownership_queue', owner: true },

  // Compliance's seventh, and the SECOND time this exact hole has been found.
  // landlord_portfolios carries both halves of Immutable Rule 4 -- the landlord
  // asks with is_listed, Orchid verifies with approved_at -- and only the
  // landlord's half was reachable. fn_admin_review_portfolio existed and
  // nothing called it, and driving it raised 23503 because approved_by
  // referenced the CONSUMER identity table while the approver is staff. Script
  // 343 repairs that, names this permission rather than 'all', and adds the
  // queue the surface reads.
  verify_landlord_business: { surface: 'landlord_business_queue', owner: true },

  // Compliance, platform script 344. The founder ruled that a unit photograph
  // goes STRAIGHT THROUGH and is REPORTABLE, and that a report hides it
  // "pending review". This desk is what "pending" means: without it a report is
  // a permanent deletion any signed-in person can perform on any landlord's
  // photograph.
  review_reported_photo: { surface: 'reported_photo_queue', owner: true },

  // Business Intelligence, platform script 345. The founder, 16 September 2026:
  // "this request must reach a desk in the Portal. Then we can gather stats
  // about how many people want to add units they don't own but manage, and
  // we'll have an idea about how to build the Agency profile."
  //
  // BI rather than Support because it is a demand signal, not a ticket -- he
  // asked for stats and for a view of who is waiting, and nothing in the
  // request is a conversation.
  view_agent_profile_interest: { surface: 'agent_profile_interest', owner: true },

  // Internal Audit. The permission is named "See raised fraud and anomaly
  // flags" and, until script 217, opened nothing: admin_flags carried 19 open
  // rows written by seven producers, its one RLS policy gated on 'all' -- 1 of
  // 6 active staff -- and no function read it at all. Measured as real seats,
  // the holder of this permission read 0 of the 19 and the 'all' holder read
  // all 19. H2's shape, in a new place.
  //
  // The retention clock rides on the same key, because fn_retention_runs is
  // gated on it too. That is recorded in the ledger as an observation rather
  // than corrected here: a permission named for fraud flags is not the
  // best-named home for POPIA retention evidence, and re-gating it is a
  // security change, not a screen-wiring one.
  view_admin_flags: { surface: 'internal_audit', owner: true },

  // Human Capital. One directory, three ways to be allowed near it.
  view_staff_directory:  { surface: 'staff_directory', owner: true },
  manage_staff_accounts: { surface: 'staff_directory' },
  assign_roles:          { surface: 'staff_directory' },

  // FIELD ONBOARDING NO LONGER LIVES IN A CARD.
  //
  //   "take the ENTIRE field onboarding section that's currently inside the
  //    Human Capital card and let it stand by itself outside all the cards, at
  //    the very top of the screen. That's going to be our focus for the next
  //    few months so it must be easily findable."
  //
  // `standsAlone` is a fourth state, and it exists so that Human Capital can go
  // on telling the truth. The two obvious alternatives both lie: deleting these
  // entries would make the card render "Not built yet" over a screen that is
  // built and in use, and leaving them as they were would draw the stand twice.
  //
  // So the card keeps naming the capability, and says where it went.
  log_field_onboarding:         { surface: 'field_onboarding', owner: true, standsAlone: true },
  view_field_onboarding_ledger: { surface: 'field_onboarding', standsAlone: true },

  // Finance. Two permissions on one screen, and they are not two halves of one
  // capability -- reading a mandate and DECRYPTING one for the bank are
  // different acts with different consequences. The reader owns the surface
  // because a dispute is the commoner errand; the export draws its own panel
  // inside it, and Canon 4.3 refuses to grant that permission directly at all,
  // so holding the first without the second is the ordinary case.
  view_debit_order_mandates:   { surface: 'debit_order_mandates', owner: true },
  export_debit_order_mandates: { surface: 'debit_order_mandates' },

  // Finance, second surface. Script 138 routes an Enterprise enquiry to
  // department 'finance' and section 'modify_subscription_tier', and this is
  // the section that permission IS -- which is why the routing addresses a
  // permission key rather than inventing a heading of its own.
  modify_subscription_tier:    { surface: 'tier_changes', owner: true },

  // Support Centre. Two different queues, and they were one built screen and
  // one absence.
  //
  // `customer_enquiries` reads contact_submissions -- the marketing site's Get
  // in Touch form. That has always had a screen.
  //
  // `support_tickets` is the other one: everything raised from INSIDE the apps.
  // It had fourteen database functions, an append-only event trail and its own
  // guard, and across all three repositories exactly ONE of those functions was
  // called by any JavaScript -- fn_tickets_on_my_desk, by Tier Changes, for a
  // single section. This comment used to say "the staff side is not [built]",
  // which was true and is the thing that got fixed.
  //
  // respond_to_ticket OWNS it because that is the permission the database
  // actually enforces: every one of fn_ticket_queue, fn_ticket_detail,
  // fn_assign_ticket and fn_move_ticket opens with
  // admin_has_permission(auth.uid(), 'respond_to_ticket'). Naming the surface
  // after the permission the database checks is what stops a card offering a
  // door the database then refuses.
  //
  // handle_staff_tickets shares it rather than opening nothing: it had a label
  // and no surface, so holding it opened no screen at all, and the internal
  // stream it names is part of this same desk.
  // FOUNDER'S RULING, 14 September: "Enquiries moves to
  // handle_customer_tickets, view_support_tickets opens the desk".
  //
  // THE OLD WIRING KEYED THE DESK TO A RETIRED PERMISSION. respond_to_ticket
  // was retired by database script 58 -- "Absorbed into view_support_tickets.
  // Which tickets a person may handle is now the grant itself:
  // handle_staff_tickets, handle_customer_tickets, or both" -- and
  // fn_my_card_permissions filters `retired_at IS NULL`, so the key never
  // reached sectionsFor and the Support tickets card was NEVER DRAWN, however
  // a seat was granted. The database would have answered; the Portal never
  // offered the door. The founder found it as "I don't see the upgrade request
  // in Support Centre".
  //
  // So the split is script 58's own, read literally: view_support_tickets is
  // the SEEING grant and the handle_* pair are the DOING grants. Database
  // script 329 moves the five ticket functions onto exactly this model, and
  // fn_assert_no_function_enforces_a_retired_permission keeps it closed.
  view_support_tickets:    { surface: 'support_tickets', owner: true },
  handle_staff_tickets:    { surface: 'support_tickets' },
  handle_customer_tickets: { surface: 'customer_enquiries', owner: true },

  // ------------------------------------------------------------------
  // LOOKING ONE SUBJECT UP, FROM FOUR DESKS.
  //
  //   "EVERY BIT OF DATA READ AND WRITTEN, ON EVERY PROFILE'S SCREEN, must be
  //    viewable by staff with the necessary permissions."
  //
  // Script 160 answers that with one dossier read from the same source tables
  // the profiles' own screens read, plus four narrower reads for the facts that
  // cost more to open. They are four SURFACES rather than one screen with four
  // tabs because they sit on four different cards, and a Finance officer opening
  // Finance should get the money -- not a screen with three sections greyed out.
  view_user_profile_summary: { surface: 'subject_summary', owner: true },

  view_billing:            { surface: 'subject_finance', owner: true },
  view_provider_seats:     { surface: 'subject_finance' },

  view_trust_events:       { surface: 'subject_trust', owner: true },
  view_admin_activity_log: { surface: 'subject_trust' },

  // The narrowest read on the platform: one job, and the whole conversation on
  // it. resolve_dispute is the permission whose existence IS the justification.
  resolve_dispute:         { surface: 'job_conversation', owner: true },

  // ------------------------------------------------------------------
  // ORCHID INSIGHTS. The card, and the file that leaves the building -- two
  // permissions on one screen for the same reason the mandates screen has two:
  // looking at a number and taking it away are different acts.
  view_platform_analytics:    { surface: 'orchid_insights', owner: true },
  export_aggregate_analytics: { surface: 'orchid_insights' },
};

/**
 * The heading a section carries.
 *
 * A permission's description reads as a capability — "See the KYC and FICA
 * verification queue" — which is the right sentence under a heading and the
 * wrong one as a heading. These are the nouns: what the section IS.
 *
 * A permission with no entry falls back to its own description, so a permission
 * added tomorrow appears as a section immediately rather than vanishing until
 * somebody remembers this file.
 */
export const SECTION_TITLES = {
  view_debit_order_mandates:   'Debit order mandates',
  export_debit_order_mandates: 'Send mandates to FNB',
  approve_verification:          'Verification queue',
  export_compliance_records:     'Compliance exports',
  flag_document_issue:           'Document problems',
  view_kyc_document:             'Identity documents',
  reattach_an_identity:          'Move an identity to the right profile',
  record_ownership_proof:        'Units waiting on proof of ownership',
  verify_landlord_business:      'Landlord businesses waiting to be listed',
  review_reported_photo:         'Reported photographs of units',
  manage_sanctions_list:         'Sanctions screening',
  view_kyc_queue:                'Applications',

  apply_score_override:          'Score corrections',
  view_admin_activity_log:       'Staff activity log',
  view_admin_flags:              'Fraud and anomaly flags',
  view_trust_events:             'Trust Events',

  resolve_dispute:               'Dispute adjudication',
  respond_to_regulatory_request: 'Regulatory requests',
  view_legal_case:               'Case files',

  export_financial_records:      'Financial exports',
  modify_subscription_tier:      'Tier changes',
  view_billing:                  'Billing and subscriptions',
  view_provider_seats:           'Provider seat rosters',

  export_aggregate_analytics:    'Aggregate exports',
  view_platform_analytics:       'Platform analytics',
  view_agent_profile_interest:   'People asking for an Agent profile',

  assign_roles:                  'Roles and grants',
  manage_staff_accounts:         'Staff accounts',
  view_staff_directory:          'Staff directory',

  log_field_onboarding:          'Field onboarding',
  view_field_onboarding_ledger:  'Field onboarding ledger',

  manage_agents:                 'Agents',
  view_agent_activity:           'Agent activity',

  // ENQUIRIES IS handle_customer_tickets' OWN SURFACE since the founder's
  // ruling of 14 September, so it is titled for the screen it opens rather than
  // for the capability. It was 'Customer tickets' and there was briefly a
  // SECOND entry for the same key further down -- a duplicate object key, which
  // JavaScript resolves silently to the last one and CRA fails the build over.
  handle_customer_tickets:       'Enquiries',
  handle_staff_tickets:          'Staff tickets',
  // NO SURFACE ENTRY ANY MORE -- respond_to_ticket was retired by database
  // script 58, so fn_my_card_permissions never returns it and no section is
  // ever built from it. The TITLE stays, because the audit log still names it
  // and a log line nobody can read is the thing retiring-rather-than-deleting
  // exists to prevent.
  respond_to_ticket:             'Support tickets',
  // The in-app ticket desk. sections.test.js caught its title missing once
  // before: a surface with no title is a screen nobody can reach.
  view_support_tickets:          'Support tickets',
  view_user_profile_summary:     'User summaries',

  manage_brand_assets:           'Brand assets',
  manage_campaigns:              'Campaigns',
  manage_opportunity_submissions: 'Opportunity submissions',
  review_opportunity_content:    'Opportunity content review',

  manage_infrastructure:         'Infrastructure',
  manage_property_taxonomy:      'Property attributes',
  manage_taxonomy:               'Service taxonomy',
  view_system_health:            'System health',

  maintain_api_keys:             'Key maintenance',
  manage_api_keys:               'API keys',
  view_integration_logs:         'Integration logs',

  manage_insights_products:      'Insights products',
  view_insights_applications:    'Insights applications',
  view_insights_customers:       'Insights customers',

  configure_opportunity_matching: 'Opportunity matching',
  manage_developer_partners:      'Developer partners',
};

/**
 * The order sections appear in, within one card.
 *
 * Reading before writing, and destructive last. Somebody opening a card is far
 * more often looking at something than changing it, and putting the queue above
 * the controls that act on it matches the order the work happens in.
 *
 * Derived from the permission's own safeguards rather than from a hand-kept
 * list, so a new permission sorts itself.
 */
export function sectionRank(permission) {
  if (permission.is_export) return 3;              // exports last: they leave the building
  if (/^(manage_|assign_|apply_|modify_|configure_|maintain_)/.test(permission.permission_key)) return 2;
  if (/^(handle_|resolve_|respond_|approve_|flag_)/.test(permission.permission_key)) return 1;
  return 0;                                        // view_* first
}

export const sectionTitle = (permission) =>
  SECTION_TITLES[permission.permission_key] || permission.description || permission.permission_key;

/**
 * Turn the permissions somebody holds in one card into the sections they see.
 *
 * A section is `built` when it owns a surface; `sharedWith` when its capability
 * acts inside a surface another permission already rendered; and neither when
 * nothing exists yet, which is most of them and is said out loud.
 */
export function sectionsFor(permissions) {
  const held = (permissions || []).filter(Boolean);

  // Which surfaces are actually reachable here: a surface is only rendered if
  // this person holds the permission that OWNS it. Somebody with
  // flag_document_issue and not approve_verification does not get the
  // verification queue drawn for them by a side door.
  // A surface that stands outside the cards is never "rendered here", however
  // it is held: it is drawn once on the home screen, and a card that also drew
  // it would be showing the same stand twice.
  const rendered = new Set(
    held
      .map((p) => SURFACES[p.permission_key])
      .filter((s) => s && s.owner && !s.standsAlone)
      .map((s) => s.surface),
  );

  return held
    .map((p) => {
      const entry = SURFACES[p.permission_key];
      const alone = Boolean(entry && entry.standsAlone);
      const owns = Boolean(entry && entry.owner && !alone);
      const sharesRendered = Boolean(entry && !entry.owner && !alone && rendered.has(entry.surface));

      return {
        key: p.permission_key,
        title: sectionTitle(p),
        blurb: p.description || '',
        surface: owns ? entry.surface : null,
        // Which built section this capability acts inside, when it does not
        // draw one of its own.
        sharedWith: sharesRendered ? entry.surface : null,
        // Built, in use, and NOT on this card. The card names it and says where
        // it went, rather than dropping it — which would read as a capability
        // this account lost — or marking it unbuilt, which would be false.
        standsAlone: alone,
        built: owns,
        requiresMfa: Boolean(p.requires_mfa),
        requiresReason: Boolean(p.requires_reason_capture),
        isExport: Boolean(p.is_export),
        rank: sectionRank(p),
      };
    })
    .sort((a, b) => (a.rank - b.rank) || a.title.localeCompare(b.title));
}

/** How many of a card's sections a person can actually work in today. */
export const builtCount = (sections) =>
  (sections || []).filter((s) => s.built).length;

/**
 * How many of a card's sections are working somewhere, whether or not that
 * somewhere is this card.
 *
 * The card's header sentence divides its sections into "these work" and "the
 * rest are named rather than hidden". A capability that stands on the home
 * screen belongs in neither half, and counting it in the second would tell
 * somebody a screen they use every day does not exist.
 */
export const workingCount = (sections) =>
  (sections || []).filter((s) => s.built || s.standsAlone || s.sharedWith).length;

/** The sections whose screens live outside every card. */
export const standingSections = (sections) =>
  (sections || []).filter((s) => s.standsAlone);
